import { useCallback } from 'react';
import { useBaseCopilotDataFlow } from '@/components/composite/CopilotControl/hooks/useBaseCopilotDataFlow';
import { CopilotQueryFlowResult } from '@/components/composite/CopilotControl/hooks/useCopilotCommunication.types';
import { Api } from '@/core/api';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { translate } from '@/core/translations';
import type { QueryCopilotResponseData } from '@/pages/api/map-events/query-copilot.types';

export interface MapCopilotDataFlowProps {
  enabled: boolean;
  question: string | undefined;
  messageId: string;
  onDataChange?: (data: QueryCopilotResponseData, query?: string) => void;
  onResponseDone?: (message: string) => void;
}

export const useMapCopilotDataFlow = ({
  enabled,
  question = '',
  messageId,
  onDataChange,
  onResponseDone,
}: MapCopilotDataFlowProps): CopilotQueryFlowResult => {
  const { orgId, sessionId, userId } = useCurrentUser();
  const { setAnswerTokens, isFetching, setIsFetching, responseType, setResponseType, error, setError, response } =
    useBaseCopilotDataFlow();

  const runQuery = useCallback(async () => {
    if (!enabled || !question || isFetching) return;

    setIsFetching(true);

    Api.copilot
      .queryMap({
        query: question,
        org_id: orgId,
        user_id: userId,
        chat_id: sessionId,
      })
      .then((resp) => {
        if (resp.type === 'filter') {
          setAnswerTokens([translate('copilot_dialog.incidents_count_found', { count: resp.incidents?.length || 0 })]);
        } else {
          setAnswerTokens([resp.response as string]);
        }
        setResponseType(resp.type);
        onDataChange?.(resp, question);
      })
      .catch((e) => setError(e as Error))
      .finally(() => {
        onResponseDone?.(question);
        setIsFetching(false);
      });
  }, [enabled, question, messageId, orgId, userId, sessionId, isFetching, onResponseDone]);

  return {
    messageId,
    response,
    citations: undefined,
    dateExtraction: undefined,
    subsetPath: '',
    responseType,
    isFetching,
    error,
    runQuery,
  };
};
